/**
 * Unified provision assist hook — INSTANCE_PROVISION (deploy + setup).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getProvisionAssistSession,
  sendProvisionAssistMessage,
} from '@/services/aiStudioAgentService';
import type { InstanceProvisionPatch } from '@/types/aiStudioAgent';
import type { InstanceSetupStatusDto } from '@/services/aiStudioInstanceSetupService';
import type { ConversationMessage, ConversationMessageRole } from '@/types/aiStudio';

export interface ProvisionAssistDraft {
  agentId?: string;
  pinnedVersionId?: string;
  instanceId?: string;
  instanceName?: string;
  industryPackId?: string | null;
  businessHierarchyId?: string | null;
  functionalHierarchyId?: string | null;
  geographicHierarchyId?: string | null;
}

export function useProvisionAssist(input: {
  agentId?: string;
  instanceId?: string;
  enabled: boolean;
}) {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [pendingSuggestion, setPendingSuggestion] = useState<InstanceProvisionPatch | null>(null);
  const [setupStatus, setSetupStatus] = useState<InstanceSetupStatusDto | null>(null);
  const draftRef = useRef<ProvisionAssistDraft>({});

  const setDraftSnapshot = useCallback((draft: ProvisionAssistDraft) => {
    draftRef.current = draft;
  }, []);

  useEffect(() => {
    if (!input.enabled || (!input.agentId && !input.instanceId)) return;
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    getProvisionAssistSession({
      agentId: input.agentId,
      instanceId: input.instanceId,
    })
      .then((session) => {
        if (cancelled) return;
        setMessages(
          session.messages.map((row) => ({
            id: row.id,
            role: row.role as ConversationMessageRole,
            content: row.content,
            createdAt: row.createdAt,
          }))
        );
        setSetupStatus(session.setupStatus);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.provision.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [input.agentId, input.instanceId, input.enabled]);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || (!input.agentId && !input.instanceId)) return;
      setSending(true);
      setErrorKey(null);
      const optimisticId = `pending-user-${Date.now()}`;
      setMessages((current) => [
        ...current,
        {
          id: optimisticId,
          role: 'user',
          content: text.trim(),
          createdAt: new Date().toISOString(),
        },
        {
          id: `pending-assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          pending: true,
        },
      ]);
      try {
        const result = await sendProvisionAssistMessage({
          agentId: input.agentId,
          instanceId: input.instanceId,
          content: text.trim(),
          draft: draftRef.current,
        });
        setMessages((current) => {
          const withoutPending = current.filter((m) => !m.pending && m.id !== optimisticId);
          return [
            ...withoutPending,
            {
              id: result.userMessage.id,
              role: 'user' as ConversationMessageRole,
              content: result.userMessage.content,
              createdAt: result.userMessage.createdAt,
            },
            {
              id: result.assistantMessage.id,
              role: 'assistant' as ConversationMessageRole,
              content: result.assistantMessage.content,
              createdAt: result.assistantMessage.createdAt,
            },
          ];
        });
        setSetupStatus(result.setupStatus);
        const patch = result.assistantMessage.suggestedProvisionPatch;
        const hasPatch =
          patch &&
          (Boolean(patch.deploy && Object.keys(patch.deploy).length > 0) ||
            Boolean(patch.setup && (patch.setup.site?.name || patch.setup.site?.location || patch.setup.setup_notes)) ||
            patch.markSetupComplete === true);
        setPendingSuggestion(hasPatch ? patch! : null);
      } catch {
        setErrorKey('aiStudio.provision.sendFailed');
        setMessages((current) => current.filter((m) => !m.pending && m.id !== optimisticId));
      } finally {
        setSending(false);
      }
    },
    [input.agentId, input.instanceId]
  );

  const clearSuggestion = useCallback(() => setPendingSuggestion(null), []);

  const refreshSession = useCallback(async () => {
    if (!input.agentId && !input.instanceId) return;
    try {
      const session = await getProvisionAssistSession({
        agentId: input.agentId,
        instanceId: input.instanceId,
      });
      setMessages(
        session.messages.map((row) => ({
          id: row.id,
          role: row.role as ConversationMessageRole,
          content: row.content,
          createdAt: row.createdAt,
        }))
      );
      setSetupStatus(session.setupStatus);
    } catch {
      // Non-blocking refresh.
    }
  }, [input.agentId, input.instanceId]);

  return {
    messages,
    loading,
    sending,
    errorKey,
    pendingSuggestion,
    setupStatus,
    send,
    clearSuggestion,
    setDraftSnapshot,
    refreshSession,
  };
}
